import React, { FC, useMemo, useContext, useEffect } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import noop from 'lodash/noop';

import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import AnalyticsContext from '../../../components/analytics/#AnalyticsContext';

export interface InjectAnalyticsScopeOptions<Props> {
  key: string | ((props: Props) => string);
  skip?: ((props: Props) => boolean);
  values?: object;
  pageValues?: object | ((props: Props) => object);
}

const injectAnalyticsScope = <Props, >({
  key,
  skip,
  values: newValues,
  pageValues: newPageValues,
}: InjectAnalyticsScopeOptions<Props>) => (WrappedComponent: FC<Props>) => {
    const InjectAnalyticsScope = (props: Props) => {
      const { scope, values, pageValues, ...analyticsContext } = useContext(AnalyticsContext);

      const pageValuesKey = useMemo(() => ({}), []);

      const nextScope = useMemo(() => {
        if (key) {
          return [
            ...scope,
            (typeof key === 'function') ? key(props) : key,
          ];
        }
        return scope;
      }, [props, scope]);

      const scopeProp = useMemo(() => nextScope.map((a) => a).join(','), [nextScope]);

      const nextValues = useMemo(() => ({
        ...values,
        ...((typeof newValues === 'function') ? newValues(props) : newValues),
      }), [props, values]);

      const newContext = useMemo(() => ({
        ...analyticsContext,
        scope: nextScope,
        values: nextValues,
        pageValues,
      }), [analyticsContext, nextScope, nextValues, pageValues]);

      const stringifiedValues = useMemo(() => JSON.stringify(nextValues), [nextValues]);

      useEffect(() => {
        if (newPageValues) {
          const resolvedPageValues: object = ((typeof newPageValues === 'function') ? newPageValues(props) : newPageValues);
          pageValues.set(pageValuesKey, resolvedPageValues);
          return () => {
            pageValues.delete(pageValuesKey);
          };
        }

        return noop;
      }, [pageValues, pageValuesKey, props]);

      if (skip && skip(props)) {
        return (
          <WrappedComponent {...props} />
        );
      }

      return (
        <AnalyticsContext.Provider value={newContext}>
          <WrappedComponent
            {...props}
            data-analytics-scope={scopeProp}
            data-analytics-values={stringifiedValues}
          />
        </AnalyticsContext.Provider>
      );
    };

    InjectAnalyticsScope.displayName = getWrappedDisplayName('InjectAnalyticsScope', WrappedComponent);

    return hoistNonReactStatics(InjectAnalyticsScope, WrappedComponent);
  };

/**
 * @component
 * @visibleName injectAnalyticsScope
 */
export default injectAnalyticsScope;
